import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';

interface OverviewGaugeChartProps {
  delayPercentage: number;
}

const OverviewGaugeChart: React.FC<OverviewGaugeChartProps> = ({ delayPercentage }) => {
  const [animatedPercentage, setAnimatedPercentage] = useState(0);
  
  useEffect(() => {
    const duration = 1000;
    const startTime = Date.now();
    const startValue = animatedPercentage;
    const endValue = delayPercentage;
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = startValue + (endValue - startValue) * easeOut;
      
      setAnimatedPercentage(currentValue);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delayPercentage]);

  const size = 200;
  const strokeWidth = 20;
  const radius = (size - strokeWidth) / 2;
  const centerX = size / 2;
  const centerY = size / 2;

  const circumference = Math.PI * radius;
  const fillPercentage = animatedPercentage / 100;
  const filledLength = circumference * fillPercentage;
  const emptyLength = circumference - filledLength;

  const backgroundArc = `M ${centerX - radius} ${centerY} A ${radius} ${radius} 0 0 1 ${centerX + radius} ${centerY}`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box sx={{ position: 'relative', width: size, height: size / 2 + 40 }}>
        <svg width={size} height={size / 2 + 10} style={{ overflow: 'visible' }}>
          <path
            d={backgroundArc}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          
          <path
            d={backgroundArc}
            fill="none"
            stroke="#ef4444"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={`${filledLength} ${emptyLength}`}
            style={{
              transition: 'stroke-dasharray 0.1s ease-out'
            }}
          />
        </svg>
        
        <Box sx={{ 
          position: 'absolute', 
          bottom: 0, 
          left: '50%', 
          transform: 'translateX(-50%)',
          textAlign: 'center'
        }}>
          <Typography variant="h4" sx={{ fontWeight: 700, fontSize: '2rem' }}>
            {Math.round(animatedPercentage)}%
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.875rem' }}>
            Delay Probability
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default OverviewGaugeChart;
